import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { classNames } from "primereact/utils";
import authApi from "../service/IdApi";
import TokenService from "../utils/TokenService";
import LocalStorageService from "../utils/LocalStorageService";
import MainMenu from "../components/MainMenu";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const WRONG_ACCOUNT_REDIRECT_SECONDS = 5;

function getNetworkHomeUrl() {
    return process.env.REACT_APP_NETWORK_SITE || "/";
}

function redirectToLogin(emailLinkUrl) {
    TokenService.setRedirectOnLogin(emailLinkUrl);
    window.location.href = process.env.REACT_APP_ID_SITE + "/login?from=email-link";
}

function redirectToDestination(destinationUrl) {
    window.location.href = destinationUrl;
}

function redirectToNetworkHome() {
    window.location.href = getNetworkHomeUrl();
}

const layoutContainerClassName = classNames(
    "layout-wrapper ",
    "pages-body",
    "login-page",
    "flex",
    "flex-column",
    "layout-menu-cyan layout-topbar-cyan",
    {
        "layout-topbar-mobile-active": false,
        "p-input-outlined": "outlined",
    }
);

const EmailLinkRedirect = () => {
    const { uuid } = useParams();
    const [switchCompany, setSwitchCompany] = useState(null);
    const [wrongAccount, setWrongAccount] = useState(false);
    const [wrongAccountCountdown, setWrongAccountCountdown] = useState(WRONG_ACCOUNT_REDIRECT_SECONDS);
    const [destinationUrl, setDestinationUrl] = useState(null);
    const [switchingProvidersInProgress, setSwitchingProvidersInProgress] = useState(false);
    const [listeningForMessages, setListeningForMessages] = useState(false);
    const [loggedOutNetworkUi, setLoggedOutNetworkUi] = useState(false);
    const [loggedOutProviderUi, setLoggedOutProviderUi] = useState(false);
    const [loggedInToNewProvider, setLoggedInToNewProvider] = useState(false);

    useEffect(() => {
        if (!uuid || !UUID_REGEX.test(uuid)) {
            redirectToNetworkHome();
            return;
        }

        const emailLinkUrl = process.env.REACT_APP_ID_SITE + "/el/" + encodeURIComponent(uuid);

        const resolveLink = async () => {
            try {
                const response = await authApi.get("auth/email-link/" + encodeURIComponent(uuid));
                const linkDestinationUrl = response.data?.destinationUrl;
                if (!linkDestinationUrl) {
                    redirectToNetworkHome();
                    return;
                }

                const authRequired = response.data.authRequired === true;
                const linkProviderId = response.data.providerId || null;

                if (!authRequired) {
                    redirectToDestination(linkDestinationUrl);
                    return;
                }

                if (!TokenService.isLoggedIn()) {
                    redirectToLogin(emailLinkUrl);
                    return;
                }

                if (!linkProviderId || TokenService.isLoggedInToProvider(linkProviderId)) {
                    redirectToDestination(linkDestinationUrl);
                    return;
                }

                const providersResult = await authApi.get("auth/providers");
                const providers = providersResult.data?.providers || [];
                let matchedProvider = null;
                for (let i = 0; i < providers.length; i++) {
                    if (providers[i]._id === linkProviderId) {
                        matchedProvider = providers[i];
                        break;
                    }
                }

                if (!matchedProvider) {
                    setWrongAccount(true);
                    return;
                }

                setDestinationUrl(linkDestinationUrl);
                setSwitchCompany(matchedProvider);
            } catch (err) {
                redirectToNetworkHome();
            }
        };

        resolveLink();
    }, [uuid]);

    useEffect(() => {
        if (!wrongAccount) {
            return;
        }

        const redirectUrl = getNetworkHomeUrl();
        const timer = setInterval(() => {
            setWrongAccountCountdown((prevCount) => {
                if (prevCount <= 1) {
                    clearInterval(timer);
                    window.location.href = redirectUrl;
                }
                return prevCount - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [wrongAccount]);

    useEffect(() => {
        if (!switchingProvidersInProgress) {
            return;
        }

        const handleMessage = (event) => {
            if (event.data && event.data.type === "wallistNetworkUi:providerCacheRefreshComplete") {
                setLoggedOutNetworkUi(true);
            }
            if (event.data && event.data.type === "wallistProviderUi:providerLogoutComplete") {
                setLoggedOutProviderUi(true);
            }
        };

        window.addEventListener("message", handleMessage);
        setListeningForMessages(true);

        return () => {
            window.removeEventListener("message", handleMessage);
            setListeningForMessages(false);
        };
    }, [switchingProvidersInProgress]);

    useEffect(() => {
        if (switchingProvidersInProgress && loggedOutNetworkUi && loggedOutProviderUi && loggedInToNewProvider) {
            if (destinationUrl) {
                redirectToDestination(destinationUrl);
            }
        }
    }, [switchingProvidersInProgress, loggedOutNetworkUi, loggedOutProviderUi, loggedInToNewProvider, destinationUrl]);

    useEffect(() => {
        if (loggedInToNewProvider && destinationUrl) {
            setTimeout(() => {
                redirectToDestination(destinationUrl);
            }, 5000);
        }
    }, [loggedInToNewProvider, destinationUrl]);

    const switchCompanyAction = async (companyId) => {
        await LocalStorageService.logoutProvider();
        try {
            const response = await authApi.post("auth/switch-provider", '{ "providerId": "' + companyId + '"}');
            TokenService.setAccessToken(response.data.userAccessToken);
            TokenService.setRefreshToken(response.data.userRefreshToken);
            setLoggedInToNewProvider(true);
        } catch (err) {
            setSwitchingProvidersInProgress(false);
            redirectToNetworkHome();
        }
    };

    const handleSwitchCompany = () => {
        if (!switchCompany) {
            return;
        }
        setSwitchingProvidersInProgress(true);
        switchCompanyAction(switchCompany._id);
    };

    const handleCancel = () => {
        redirectToNetworkHome();
    };

    if (wrongAccount) {
        const redirectUrl = getNetworkHomeUrl();
        return (
            <div className={layoutContainerClassName}>
                <MainMenu />
                <div className="align-self-center mt-auto mb-auto">
                    <div className="pages-panel card flex flex-column p-4">
                        <h4>This link was intended for another account.</h4>
                        <div className="text-lg">You will be redirected in {wrongAccountCountdown} seconds to:</div>
                        <div className="mt-2">
                            <a href={redirectUrl}>{redirectUrl}</a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (switchCompany) {
        return (
            <div>
                <MainMenu />
                <div className="layout-content">
                    <div className="card flex flex-column align-items-center justify-content-center py-6 px-4" style={{ maxWidth: "550px", margin: "2rem auto" }}>
                        <div className="text-xl text-center mb-4">
                            This link was intended for <strong>{switchCompany.name}</strong>.
                        </div>
                        <div className="flex flex-row gap-2">
                            <Button label={"Switch to " + switchCompany.name} icon="pi pi-sync" onClick={handleSwitchCompany} />
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={handleCancel} />
                        </div>
                    </div>
                </div>
                {switchingProvidersInProgress && (
                    <Dialog
                        visible={switchingProvidersInProgress}
                        style={{ width: "225px" }}
                        header="Switching Accounts"
                        modal
                        closable={false}
                    >
                        <div className="flex flex-column justify-content-center w-full" style={{ height: "75px" }}>
                            <div className="text-lg text-center">
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                            </div>
                            {listeningForMessages && (
                                <>
                                    <iframe
                                        src={process.env.REACT_APP_NETWORK_SITE + "/refresh-provider-cache"}
                                        style={{ width: 1, height: 1, border: "none" }}
                                        title="refresh-provider-cache"
                                    />
                                    <iframe
                                        src={process.env.REACT_APP_PROVIDER_SITE + "/logout"}
                                        style={{ width: 1, height: 1, border: "none" }}
                                        title="provider-logout"
                                    />
                                </>
                            )}
                        </div>
                    </Dialog>
                )}
            </div>
        );
    }

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "#ffffff",
            }}
        />
    );
};

export default EmailLinkRedirect;
